import type { PointCloudCapabilityProvider } from '@trimble/web3d-providers';
import { ScanDataApiService } from '@data-access/geospatial/scan-data';
import { CrsResolver } from '@data-access/coordinates';
import { TcRegion } from '@data-access/connect/tc-tcps';
import { Vector3 } from '@technology/web3d';
import { Point2D } from '@trimble/utilities';
import { fetchPotreeBoundingCenter } from './fetch-potree-bounding-center';

export interface PointCloudContent {
  selectedRegion: TcRegion;
  selectedProjectId: string;
  selectedPointCloudId: string;
}

export interface PointCloudGeoReference {
  csib: string | undefined;
  center: Point2D | undefined;
}

async function fetchPointCloudCsibAndUrl(
  content: PointCloudContent,
  scanDataApi: ScanDataApiService,
  crsResolver: CrsResolver,
  signal: AbortSignal
): Promise<{ url: string; csib: string | undefined }> {
  signal.throwIfAborted();

  const [pointCloudUrlId, pointCloudData] = await Promise.all([
    scanDataApi.getPointCloudUrlIdQuery(
      content.selectedRegion,
      content.selectedProjectId,
      content.selectedPointCloudId,
      signal
    ),
    scanDataApi.getPointCloudQuery(
      content.selectedRegion,
      content.selectedProjectId,
      content.selectedPointCloudId,
      signal
    ),
  ]);

  signal.throwIfAborted();

  const csib = await crsResolver.resolveCsib(pointCloudData.crs);
  return { url: pointCloudUrlId.url, csib };
}

/**
 * CSIB + tileset-metadata center with no 3D viewer. Map-only layouts never
 * register a PointCloudCapabilityProvider, so loadItem never runs.
 */
export async function resolvePointCloudGeoWithoutViewer(
  content: PointCloudContent,
  scanDataApi: ScanDataApiService,
  crsResolver: CrsResolver,
  signal: AbortSignal
): Promise<PointCloudGeoReference> {
  const { url, csib } = await fetchPointCloudCsibAndUrl(
    content,
    scanDataApi,
    crsResolver,
    signal
  );
  signal.throwIfAborted();
  const center = await fetchPotreeBoundingCenter(url, signal);
  signal.throwIfAborted();
  return { csib, center };
}

export async function loadPointCloud(
  content: PointCloudContent,
  scanDataApi: ScanDataApiService,
  crsResolver: CrsResolver,
  pointCloudProvider: PointCloudCapabilityProvider,
  /**
   * If true, the trajectory will be unloaded before loading it again.
   * If false and the trajectory is already loaded it will not be reloaded.
   */
  reload: boolean,
  signal: AbortSignal
): Promise<PointCloudGeoReference> {
  const { url, csib } = await fetchPointCloudCsibAndUrl(
    content,
    scanDataApi,
    crsResolver,
    signal
  );

  signal.throwIfAborted();

  const model = await pointCloudProvider.loadPointCloud(
    url,
    content.selectedPointCloudId,
    false,
    csib,
    reload
  );

  // World-coordinate center from the model's bounding box, forwarded to
  // DataContext.currentLoadedCenter for 2D basemap positioning.
  const centerVec = model?.getModelBoundingBox().getCenter(new Vector3());
  const center: Point2D | undefined = centerVec
    ? { x: centerVec.x, y: centerVec.y }
    : undefined;

  return { csib, center };
}
